import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import CardChartTotal from '@/components/ui/card-wrapper/CardChartTotal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MonthDropdown from '@/components/ui/date-picker/MonthDropdown';
import { Download, Activity, Clock, CheckCircle2, Coins } from 'lucide-react';
import { useOCRMetrics } from '@/service/gpu-instance/ocr-hook';
import { OCRTimeRange } from '@/service/gpu-instance/ocr-type';
import { downloadCSV } from '@/utils/csv';
import ActivityTable from './components/ActivityTable';

// Chart configuration
const requestsChartConfig: ChartConfig = {
  requests: {
    label: 'Requests',
    color: 'hsl(var(--chart-1))',
  },
};

const latencyChartConfig: ChartConfig = {
  latency: {
    label: 'Latency (ms)',
    color: 'hsl(var(--chart-2))',
  },
};

const OCRMetricsPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [timeRange, setTimeRange] = useState<OCRTimeRange>('7d');
  const [customMonth, setCustomMonth] = useState(dayjs().format('YYYY-MM'));

  const { data: metricsData, isLoading } = useOCRMetrics({
    instanceId: instanceId || '',
    timeRange,
    customMonth: timeRange === 'month' ? customMonth : undefined,
  });

  // Transform data for charts
  const requestsChartData = useMemo(() => {
    if (!metricsData?.data_points) return [];
    return metricsData.data_points.map((dp) => ({
      date: dp.timestamp,
      requests: dp.requests,
    }));
  }, [metricsData]);

  const latencyChartData = useMemo(() => {
    if (!metricsData?.data_points) return [];
    return metricsData.data_points.map((dp) => ({
      date: dp.timestamp,
      latency: dp.latency_ms,
    }));
  }, [metricsData]);

  // Handle CSV export
  const handleDownloadCSV = () => {
    if (!metricsData?.data_points) return;

    const csvData = metricsData.data_points.map((dp) => ({
      timestamp: dayjs.unix(dp.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      requests: dp.requests,
      latency_ms: dp.latency_ms,
      success_count: dp.success_count,
      error_count: dp.error_count,
      tokens: dp.tokens,
    }));

    downloadCSV({
      data: csvData,
      filename: `ocr-metrics-${instanceId}-${timeRange}.csv`,
    });
  };

  const handleMonthSelect = (selectedMonth: string) => {
    setCustomMonth(selectedMonth);
    setTimeRange('month');
  };

  // Calculate time domain for charts
  const timeDomain = useMemo(() => {
    if (!metricsData?.metrics.time_range) {
      return [dayjs().subtract(7, 'day').unix(), dayjs().unix()];
    }
    return [metricsData.metrics.time_range.start, metricsData.metrics.time_range.end];
  }, [metricsData]);

  return (
    <div className="container mx-auto py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">API Metrics</h1>
          <p className="text-sm text-muted-foreground">
            Monitor your OCR API usage and performance
          </p>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Tabs
            value={timeRange}
            onValueChange={(v) => setTimeRange(v as OCRTimeRange)}
          >
            <TabsList>
              <TabsTrigger value="24h">24h</TabsTrigger>
              <TabsTrigger value="7d">7d</TabsTrigger>
              <TabsTrigger value="30d">30d</TabsTrigger>
              <TabsTrigger value="month">Month</TabsTrigger>
            </TabsList>
          </Tabs>

          {timeRange === 'month' && (
            <MonthDropdown
              onSelect={handleMonthSelect}
              defaultMonth={customMonth}
            />
          )}
        </div>

        <Button variant="secondary" onClick={handleDownloadCSV}>
          <Download className="mr-2 h-4 w-4" />
          Download CSV
        </Button>
      </div>

      {isLoading ? (
        <div className="flex h-[400px] items-center justify-center">
          <div className="text-muted-foreground">Loading metrics...</div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Metric Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Requests
                </CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metricsData?.metrics.total_requests.toLocaleString() || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  API calls in selected period
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Avg Latency
                </CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metricsData?.metrics.avg_latency_ms.toLocaleString() || 0} ms
                </div>
                <p className="text-xs text-muted-foreground">
                  Average response time
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Success Rate
                </CardTitle>
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metricsData?.metrics.success_rate || 0}%
                </div>
                <p className="text-xs text-muted-foreground">
                  Successful requests
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Tokens
                </CardTitle>
                <Coins className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metricsData?.metrics.total_tokens.toLocaleString() || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Tokens consumed
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Requests Chart */}
            <CardChartTotal
              title="Request Volume"
              description="Number of API requests over time"
              total={metricsData?.metrics.total_requests.toLocaleString()}
              totalLabel="Total"
            >
              <ChartContainer config={requestsChartConfig} className="max-h-[300px] w-full">
                <LineChart
                  data={requestsChartData}
                  margin={{ left: 12, right: 12 }}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={32}
                    tickFormatter={(value) => dayjs.unix(value).format('DD MMM')}
                    scale="time"
                    domain={timeDomain}
                    type="number"
                  />
                  <YAxis tickLine={false} axisLine={false} tickMargin={8} tickCount={5} />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        className="w-[150px]"
                        labelFormatter={(_, payload) => {
                          return dayjs.unix(payload[0]?.payload?.date).format('DD MMM, HH:mm');
                        }}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="requests"
                    stroke="var(--color-requests)"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ChartContainer>
            </CardChartTotal>

            {/* Latency Chart */}
            <CardChartTotal
              title="Response Latency"
              description="Average response time in milliseconds"
              total={`${metricsData?.metrics.avg_latency_ms.toLocaleString() || 0} ms`}
              totalLabel="Avg"
            >
              <ChartContainer config={latencyChartConfig} className="max-h-[300px] w-full">
                <LineChart
                  data={latencyChartData}
                  margin={{ left: 12, right: 12 }}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={32}
                    tickFormatter={(value) => dayjs.unix(value).format('DD MMM')}
                    scale="time"
                    domain={timeDomain}
                    type="number"
                  />
                  <YAxis tickLine={false} axisLine={false} tickMargin={8} tickCount={5} />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        className="w-[150px]"
                        labelFormatter={(_, payload) => {
                          return dayjs.unix(payload[0]?.payload?.date).format('DD MMM, HH:mm');
                        }}
                        valueFormatter={(value) => `${value} ms`}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="latency"
                    stroke="var(--color-latency)"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ChartContainer>
            </CardChartTotal>
          </div>

          {/* Activity Table */}
          <ActivityTable />
        </div>
      )}
    </div>
  );
};

export default OCRMetricsPage;
